import { handle } from 'redux-pack';

import { AvailableInstance, StripeStatus } from '../../../connection/integrations';
import { Action } from '../../../types/actions';
import { IntegratedEventsDB, IntegrationsMappedDB } from '../../../types/integrations';
import { ISSOIntegration, Oauth2Integration } from "../../../types/working-model";
import {
	CLEAR_INSTANCE_TO_CREATE,
	CREATE_TRAY_INSTANCE,
	DISABLE_GOOGLE_ANALYTICS,
	GET_GOOGLE_ANALYTICS,
	GET_TRAY_SOLUTIONS,
	GET_TRAY_USER,
	GET_USER_SOLUTION_INSTANCES,
	INTEGRATIONS_STRIPE_CONTACT,
	INTEGRATIONS_STRIPE_STATUS,
	SET_GOOGLE_ANALYTICS,
	TOGGLE_TRAY_INSTANCE,
	GET_SSO_INTEGRATIONS,
	GET_OAUTH2_INTEGRATION,
	UPDATE_OAUTH2_INTEGRATION,
	TOGGLE_OAUTH2_INTEGRATION,
	TOGGLE_OAUTH2_ADMIN_USE,
	DELETE_OAUTH2_INTEGRATION,
	GET_PUBLIC_OAUTH2_INTEGRATION,
	CREATE_OAUTH2_INTEGRATION,
	GET_EVENT_INSTANCES,
	GET_CHANNEL_MAPPED_INSTANCES,
	CREATE_TRAY_INSTANCE_MAPPED,
	TOGGLE_CUSTOM_MAPPED_INSTANCE,
	TOGGLE_EVENT_OVERRIDES_INSTANCE
} from '../../actions/admin/integrations';

export enum EIntegrationType {
	registration = 1,
	inbound = 2,
	activity = 3 // activity tracking integrations (event/session attendance)
	// add more types when defined
}

export interface ITrayUser {
	user: number;
	tray_id: string;
	channel: number;
	name: string;
	integrations: number[] | null;
	status: number;
}
export interface ITraySolutionInstance {
	authValues: {
		authId: string;
		externalId: string;
	}[];
	configValue: any[];
	configValues: any[];
	created: string;
	enabled: boolean;
	id: string;
	name: string;
	owner: string;
	solutionVersionFlags: {
		hasNewerVersion: boolean;
		requiresSystemInputToUpdateVersion: boolean;
		requiresUserInputToUpdateVersion: boolean;
	}
	solution: {
		id: string;
	}
	workflows: any[];
}

export interface ITraySolution {
	description: string;
	id: string;
	tags: string[];
	title: string;
}

// The tag values will be used to filter
export const SolutionTagTypes = {
	// Environment tag types
	Environment: {
		development: "stage_development",
		production: "stage_production",
		// add more types as they are added to workflow
	},

	// Instance type tags, these will determine where in the live event flow the data is sent
	// as well as the format of data that is sent
	InstanceType: {
		// registration type sends all registration data gathered from every event owned by a given channel, to th endpoint of the instance 
		registration: "type_registration",

		// inbound defines an instance that is receiving data from am external source, and storing it within the app 
		inbound: "type_inbound",

		// activity defines an integration that can be triggered by multiple different user interactions on the event page
		activity: "type_activity",
		// add more tag types as we add different integration types
	},

	// Option tags are miscellaneous options that can be added to a solution instance
	// These options allow different interactions than the traditional integrations workflow
	Options: {
		// no auth defines an instance that doesn't require any authentication values to enable
		no_auth: "no_auth",

		// show passcodes defines an instance that displays a list of passcode lists when configuring the instance
		// more information about this can be found in the Tray Reference doc
		show_passcodes: "show_passcodes",

		// indicates this is an integration that follows the new custom mapping flow.
		// These integrations are treated differently than the standard integrations in both appearance and behavior.
		custom_mapping: "mapping"
	}
};

export const SolutionConfigKeys = {
	// NOTE: tray io state that all config values that are accessed through their api must be prefixed with the string "external_"
	// These config keys will need to be verified in the tray workflow
	external_passcode: "external_passcode",
	external_mapping: "external_mapping"
};

export interface ITrayInstanceToCreate {
	instanceId: string;
	solutionId: string;
}
export interface IntegrationsState {
	stripe: {
		loading: boolean
		status: StripeStatus,
		contact: string | null,
		setting_contact: boolean
	},
	googleAnalytics: {
		id: number;
		google_id: string;
		channel: number;
		status: number;
	} | null;
	loadingGoogleAnalytics: boolean;
	creatingIntegrationsId: boolean;
	trayInstances: ITraySolutionInstance[];
	loadingInstances: boolean;
	traySolutions: ITraySolution[];
	loadingSolutions: boolean;
	solutionsError: boolean;
	loadingTrayUser: boolean;
	trayUser: ITrayUser | null;
	instanceToCreate: ITraySolutionInstance | null;
	customSSOs?: ISSOIntegration[];
	oauthIntegration?: Oauth2Integration;
	publicOauthIntegration?: Partial<Oauth2Integration>;
	loadingOauthIntegration: boolean;
	eventInstances: AvailableInstance[];
	loadingEventInstances: boolean;
	instancesMapped: IntegrationsMappedDB[];
	loadingInstancesMapped: boolean;
	failedInstancesMapped: boolean;
	creatingMappedInstance: boolean;
	failedCreateMappedInstance: boolean;
}

const initialState: IntegrationsState = {
	stripe: {
		loading: false,
		status: StripeStatus.none,
		contact: null,
		setting_contact: false
	},
	googleAnalytics: null,
	loadingGoogleAnalytics: false,
	creatingIntegrationsId: false,
	trayInstances: [],
	loadingInstances: false,
	traySolutions: [],
	loadingSolutions: false,
	solutionsError: false,
	loadingTrayUser: false,
	trayUser: null,
	instanceToCreate: null,
	loadingOauthIntegration: false,
	eventInstances: [],
	loadingEventInstances: false,
	instancesMapped: [],
	loadingInstancesMapped: false,
	failedInstancesMapped: false,
	creatingMappedInstance: false,
	failedCreateMappedInstance: false
};

export default function IntegrationsReducer(state: IntegrationsState = initialState, action: Action): IntegrationsState {
	switch (action.type) {
		case INTEGRATIONS_STRIPE_STATUS: {
			return handle(state, action, {
				start: state => ({ ...state, stripe: { ...state.stripe, loading: true } }),
				success: state => ({
					...state,
					stripe: {
						...state.stripe,
						status: action.payload.stripe_status,
						contact: action.payload.primary_contact
					}
				}),
				finish: state => ({
					...state,
					stripe: {
						...state.stripe,
						loading: false
					}
				})
			});
		}
		case INTEGRATIONS_STRIPE_CONTACT: {
			return handle(state, action, {
				start: state => ({
					...state,
					stripe: {
						...state.stripe,
						setting_contact: true
					}
				}),
				success: state => ({
					...state,
					stripe: {
						...state.stripe,
						contact: action.payload.email
					}
				}),
				finish: state => ({
					...state,
					stripe: {
						...state.stripe,
						setting_contact: false
					}
				})
			});
		}
		case GET_GOOGLE_ANALYTICS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingGoogleAnalytics: true }),
				finish: state => ({ ...state, loadingGoogleAnalytics: false }),
				success: state => {
					if (action.payload.not_found) {
						return ({ ...state, googleAnalytics: null });
					} else {
						return ({ ...state, googleAnalytics: action.payload });
					}
				}
			});
		}
		case SET_GOOGLE_ANALYTICS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingGoogleAnalytics: true }),
				finish: state => ({ ...state, loadingGoogleAnalytics: false }),
				success: state => ({ ...state, googleAnalytics: action.payload })
			});
		}
		case DISABLE_GOOGLE_ANALYTICS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingGoogleAnalytics: true }),
				finish: state => ({ ...state, loadingGoogleAnalytics: false }),
				success: state => ({ ...state, googleAnalytics: null })
			});
		}
		case GET_USER_SOLUTION_INSTANCES: {
			return handle(state, action, {
				start: state => ({ ...state, loadingInstances: true }),
				finish: state => ({ ...state, loadingInstances: false }),
				success: state => ({ ...state, trayInstances: action.payload })
			});
		}
		case GET_TRAY_SOLUTIONS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingSolutions: true }),
				finish: state => ({ ...state, loadingSolutions: false }),
				failure: state => ({ ...state, solutionsError: true }),
				success: state => ({ ...state, traySolutions: action.payload, solutionsError: false })
			});
		}
		case CREATE_TRAY_INSTANCE: {
			return handle(state, action, {
				start: state => ({ ...state, loadingInstances: true }),
				finish: state => ({ ...state, loadingInstances: false }),
				success: state => ({
					...state,
					trayInstances: [...state.trayInstances, action.payload.instance],
					trayUser: action.payload.user,
					instanceToCreate: action.payload.instance
				})
			});
		}
		case TOGGLE_TRAY_INSTANCE: {
			const updatedInstance = action.payload;
			// update matching instance enabled value with new value returned from update call
			return handle(state, action, {
				success: state => ({
					...state,
					trayInstances: state.trayInstances.map((instance: ITraySolutionInstance) => {
						if (instance.id === updatedInstance.id) {
							return {
								...instance,
								enabled: updatedInstance.enabled
							};
						} else {
							return instance;
						}
					})
				})
			});
		}
		case TOGGLE_CUSTOM_MAPPED_INSTANCE: {
			if (!action.payload) return state;
			const updatedInstance: IntegrationsMappedDB = action.payload;
			return handle(state, action, {
				success: state => ({
					...state,
					instancesMapped: state.instancesMapped.map((instance: IntegrationsMappedDB) => {
						if (instance.instance_uuid === updatedInstance.instance_uuid) {
							return updatedInstance;
						}
						return instance;
					})
				})
			});
		}
		case TOGGLE_EVENT_OVERRIDES_INSTANCE: {
			if (!action.payload) return state;
			return handle(state, action, {
				success: state => {
					const { added, removed }: { added?: IntegratedEventsDB[], removed?: IntegratedEventsDB[] } = action.payload;
					if (added?.length) {
						const newInstances = state.eventInstances.map(instance => {
							if (instance.instance_uuid === added[0].instance_uuid) {
								return {
									...instance,
									integrations_event: added[0]
								};
							}
							return instance;
						});
						return {
							...state,
							eventInstances: newInstances
						};
					}
					if (removed?.length) {
						const newInstances = state.eventInstances.map(instance => {
							if (instance.instance_uuid === removed[0].instance_uuid) {
								return {
									...instance,
									integrations_event: undefined
								};
							}
							return instance;
						});
						return {
							...state,
							eventInstances: newInstances
						};
					}
					return state;
				}
			});
		}

		case GET_TRAY_USER: {
			return handle(state, action, {
				start: state => ({ ...state, loadingTrayUser: true }),
				finish: state => ({ ...state, loadingTrayUser: false }),
				success: state => ({ ...state, trayUser: action.payload })
			});
		}
		case CLEAR_INSTANCE_TO_CREATE: {
			return { ...state, instanceToCreate: null };
		}
		case GET_SSO_INTEGRATIONS: {
			return handle(state, action, {
				success: state => ({
					...state,
					customSSOs: action.payload
				})
			});
		}
		case GET_OAUTH2_INTEGRATION: {
			return handle(state, action, {
				start: state => ({ ...state, loadingOauthIntegration: true }),
				success: state => ({ ...state, oauthIntegration: action.payload }),
				finish: state => ({ ...state, loadingOauthIntegration: false })
			});
		}
		case GET_PUBLIC_OAUTH2_INTEGRATION: {
			return handle(state, action, {
				start: state => ({ ...state, loadingOauthIntegration: true }),
				success: state => ({ ...state, publicOauthIntegration: action.payload }),
				finish: state => ({ ...state, loadingOauthIntegration: false })
			});
		}
		case CREATE_OAUTH2_INTEGRATION:
		case UPDATE_OAUTH2_INTEGRATION: {
			return handle(state, action, {
				success: state => ({ ...state, oauthIntegration: action.payload })
			});
		}
		case TOGGLE_OAUTH2_INTEGRATION: {
			return handle(state, action, {
				success: state => {
					if (state.oauthIntegration) {
						return ({ ...state, oauthIntegration: { ...state.oauthIntegration, active: !state.oauthIntegration.active } });
					} else {
						return state;
					}
				}
			});
		}

		case TOGGLE_OAUTH2_ADMIN_USE: {
			return handle(state, action, {
				success: state => {
					if (state.oauthIntegration) {
						return ({ ...state, oauthIntegration: { ...state.oauthIntegration, admin_login: !state.oauthIntegration.admin_login } });
					} else {
						return state;
					}
				}
			});
		}

		case DELETE_OAUTH2_INTEGRATION: {
			return handle(state, action, {
				success: state => ({ ...state, oauthIntegration: undefined })
			});
		}
		case GET_EVENT_INSTANCES:
			return handle(state, action, {
				start: state => ({ ...state, loadingEventInstances: true }),
				finish: state => ({ ...state, loadingEventInstances: false }),
				success: state => {
					return {
						...state,
						eventInstances: action.payload.all_instances
					};
				}
			});
		case GET_CHANNEL_MAPPED_INSTANCES: {
			return handle(state, action, {
				start: state => ({ ...state, loadingInstancesMapped: true }),
				success: state => ({ ...state, instancesMapped: action.payload, failedInstancesMapped: false }),
				finish: state => ({ ...state, loadingInstancesMapped: false }),
				failure: state => ({ ...state, failedInstancesMapped: true })
			});
		}
		case CREATE_TRAY_INSTANCE_MAPPED: {
			return handle(state, action, {
				start: state => ({ ...state, creatingMappedInstance: true }),
				success: state => ({ ...state, instancesMapped: [...state.instancesMapped, action.payload.instance], failedCreateMappedInstance: false }),
				finish: state => ({ ...state, creatingMappedInstance: false }),
				failure: state => ({ ...state, failedCreateMappedInstance: true })
			});
		}
		default: return state;
	}
}
